/* Aufwand für den Compiler, schon beim Mappen sichtbar: Welche Brushes erzeugen BSP-Zellen und Portale (VIS-Zeit)?
 * Grundlage sind Messungen mit SDHLT an einer großen Außenkarte (siehe docs/architektur.md, „Compile-Aufwand“):
 *  - VIS-Zeit hängt an der Zahl der Portale; jeder normale Welt-Brush schneidet den Raum und erzeugt welche.
 *  - func_detail zählt für VIS nur mit zhlt_detaillevel >= 1; ohne den Schlüssel ist der Brush normale Welt.
 *  - Die Plane-Zahl, die CSG meldet, ist vor der Optimierung und kein Limit (die BSP braucht davon ein Fünftel).
 * Die Schwellen sind Faustwerte, keine Garantien. Ohne DOM. */
import type { Brush, CheckFix, Doc, Vec3 } from './types.ts';
import { bbox, faceNormal } from './brush.ts';

export interface Complexity {
  /** Brushes der Welt und aller func_detail (ohne reine Clip-/Sky-/Hint-Brushes) */
  total: number;
  /** Brushes, die für VIS zählen: Welt plus func_detail ohne zhlt_detaillevel >= 1 */
  structural: number;
  /** davon mit mindestens einer nicht achsparallelen Fläche */
  sloped: number;
  /** davon Dreiecks-Prismen (Gelände-/Keil-Brushes: 5 Flächen, 6 Ecken) */
  prisms: number;
  /** Brushes in echtem Detail (zhlt_detaillevel >= 1) */
  detail: number;
  /** func_detail-Entities ohne zhlt_detaillevel >= 1 und ihre Brushes */
  detailNoLevelEnts: number;
  detailNoLevelBrushes: number;
  /** schräge, zählende Brushes, die ganz unter der Oberfläche eines Wasser-Brushes (Textur !…) liegen */
  underwater: number;
  /** Zuordnung der Zähler zu den betroffenen Objekten (Brush- und Entity-IDs) */
  ids: { detailNoLevelB: string[]; detailNoLevelE: string[]; prisms: string[]; structural: string[]; underwater: string[] };
}

export type FindingId = 'detail_level' | 'prisms_in_world' | 'vis_load' | 'underwater';
export interface Finding { id: FindingId; level: 'warn' | 'err'; params: Record<string, number>; sel: { b: string[]; e: string[] }; fix?: CheckFix }

/** Schwellen (Faustwerte aus Messungen) */
export const LIMITS = { prismsWarn: 300, prismsErr: 1000, structuralWarn: 1200, structuralErr: 2500, underwaterWarn: 150 } as const;

const AX = 0.9999;
const isSlopedFace = (b: Brush, vi: number[]): boolean => { const n = faceNormal(b.verts, vi); return Math.max(Math.abs(n[0]), Math.abs(n[1]), Math.abs(n[2])) <= AX; };
/** Brushes, die nur der Compiler auswertet (Clip, Null, Hint …) und die keine Welt-Fläche zeichnen */
const isSpecialBrush = (b: Brush): boolean => b.faces.every((f) => /^(clip|null|skip|hint|solidhint|origin|bevel|aaatrigger)$/i.test(f.tex));
/** Dreiecks-Prisma: genau zwei Dreiecksflächen und drei Vierecke */
const isPrism = (b: Brush): boolean => b.faces.length === 5 && b.verts.length === 6 && b.faces.filter((f) => f.vi.length === 3).length === 2;
const detailLevel = (kv: Record<string, string>): number => { const n = parseInt(kv.zhlt_detaillevel || '', 10); return Number.isFinite(n) ? n : 0; };

export function analyze(doc: Doc): Complexity {
  const c: Complexity = { total: 0, structural: 0, sloped: 0, prisms: 0, detail: 0, detailNoLevelEnts: 0, detailNoLevelBrushes: 0, underwater: 0, ids: { detailNoLevelB: [], detailNoLevelE: [], prisms: [], structural: [], underwater: [] } };
  // Wasseroberfläche und -ausdehnung aus Brushes mit Wassertextur (!…)
  const water: { min: Vec3; max: Vec3 }[] = [];
  doc.ents.forEach((e) => e.brushes.forEach((b) => { if (b.faces.some((f) => f.tex.charAt(0) === '!')) water.push(bbox(b)); }));
  const under = (b: Brush): boolean => {
    const x = bbox(b);
    return water.some((w) => x.max[2] <= w.max[2] + 0.5 && (x.min[0] + x.max[0]) / 2 >= w.min[0] && (x.min[0] + x.max[0]) / 2 <= w.max[0] && (x.min[1] + x.max[1]) / 2 >= w.min[1] && (x.min[1] + x.max[1]) / 2 <= w.max[1] && x.min[2] >= w.min[2] - 0.5);
  };
  doc.ents.forEach((e, i) => {
    const cls = e.kv.classname;
    const isDetail = cls === 'func_detail';
    if (i !== 0 && !isDetail) return;
    const real = isDetail && detailLevel(e.kv) >= 1;
    if (isDetail && !real) { c.detailNoLevelEnts++; c.detailNoLevelBrushes += e.brushes.length; e.brushes.forEach((b) => c.ids.detailNoLevelB.push(b.id)); }
    e.brushes.forEach((b) => {
      if (isSpecialBrush(b) || b.faces.some((f) => f.tex.charAt(0) === '!')) return;
      c.total++;
      if (real) { c.detail++; return; }
      c.structural++; c.ids.structural.push(b.id);
      const sloped = b.faces.some((f) => isSlopedFace(b, f.vi));
      if (sloped) { c.sloped++; if (under(b)) { c.underwater++; c.ids.underwater.push(b.id); } }
      if (isPrism(b)) { c.prisms++; c.ids.prisms.push(b.id); }
    });
  });
  return c;
}

/** Übersetzungsschlüssel je Befund (als Tabelle, damit der i18n-Test sie findet) */
export const FINDING_KEYS: Record<FindingId, string> = { detail_level: 'cx.detail_level', prisms_in_world: 'cx.prisms_in_world', vis_load: 'cx.vis_load', underwater: 'cx.underwater' };

export function findings(c: Complexity): Finding[] {
  const out: Finding[] = [];
  if (c.detailNoLevelEnts) out.push({ id: 'detail_level', level: 'warn', params: { ents: c.detailNoLevelEnts, brushes: c.detailNoLevelBrushes }, sel: { b: c.ids.detailNoLevelB, e: c.ids.detailNoLevelE }, fix: 'detail' });
  if (c.prisms >= LIMITS.prismsWarn) out.push({ id: 'prisms_in_world', level: c.prisms >= LIMITS.prismsErr ? 'err' : 'warn', params: { count: c.prisms }, sel: { b: c.ids.prisms, e: [] }, fix: 'detail' });
  if (c.structural >= LIMITS.structuralWarn) out.push({ id: 'vis_load', level: c.structural >= LIMITS.structuralErr ? 'err' : 'warn', params: { count: c.structural, detail: c.detail }, sel: { b: c.ids.structural, e: [] } });
  if (c.underwater >= LIMITS.underwaterWarn) out.push({ id: 'underwater', level: 'warn', params: { count: c.underwater }, sel: { b: c.ids.underwater, e: [] } });
  return out;
}
